import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ja } from 'date-fns/locale/ja';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { apiClient } from '../../lib/apiClient';
import { useDivisionOptions, useTeamOptions } from '../../state/master';
import MultiSelectWithCheckboxes, { Suggestion } from '../common/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';
import MultiSelectCheckbox, { Option as TeamOption } from '../common/MultiSelectCheckbox/MultiSelectCheckbox';
import ConfirmDialog from '../common/ConfirmDialog';
import { Snackbar } from '../common/Snackbar/Snackbar';
import { formatVersionDt } from '../common/commonFunc';

type SyncStatusRow = {
  wr_id: number;
  title: string | null;
  department_id: number;
  team_id: number;
  create_dt: string | null;
  document_id: number | null;
  document_docset_id: number | null;
  current_docset_id: number | null;
  linked: boolean;
  mismatch: boolean;
};

type RejoinResult = {
  wr_id: number;
  success: boolean;
  error?: string;
};

const EMPTY_SUGGESTIONS: Suggestion[] = [];

// 絞り込み行の各項目でラベル・コントロールの高さを揃えるための共通スタイル
const FILTER_LABEL_SX = { display: 'block', mb: 0.5, lineHeight: '18px' } as const;
const FILTER_CONTROL_SX = { '& .MuiInputBase-root': { height: 40 } } as const;

const EnlightSyncCheck: React.FC = () => {
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const departmentSuggestions: Suggestion[] = useMemo(
    () =>
      (Array.isArray(divisionOptions) ? divisionOptions : []).map((d: any) => ({
        id: Number(d.department_id),
        label: String(d.label ?? d.value ?? ''),
      })),
    [divisionOptions]
  );

  // チームは週報連画面の検索条件と同じ MultiSelectCheckbox（手動入力での絞り込み＋複数選択＋全選択）を使う
  const allTeamOptions: TeamOption[] = useMemo(
    () =>
      (Array.isArray(teamOptions) ? teamOptions : []).map((t: any) => ({
        id: String(t.team_id),
        value: String(t.label ?? t.value ?? ''),
        department_id: String(t.department_id ?? ''),
      })),
    [teamOptions]
  );

  const departmentLabelById = useMemo(() => {
    const m = new Map<number, string>();
    departmentSuggestions.forEach((d) => m.set(d.id, d.label));
    return m;
  }, [departmentSuggestions]);
  const teamLabelById = useMemo(() => {
    const m = new Map<number, string>();
    allTeamOptions.forEach((t) => m.set(Number(t.id), t.value));
    return m;
  }, [allTeamOptions]);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedDepts, setSelectedDepts] = useState<Suggestion[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // チームは課に連動させる：課が未選択の間は選択自体をさせず（Select自体をdisabled）、
  // 課を選択した時点でその課に属するチームのみを候補にする。
  const teamOptionsFiltered: TeamOption[] = useMemo(() => {
    if (selectedDepts.length === 0) return [];
    const deptIds = new Set(selectedDepts.map((d) => String(d.id)));
    return allTeamOptions.filter((t) => t.department_id && deptIds.has(String(t.department_id)));
  }, [allTeamOptions, selectedDepts]);

  // 課の絞り込みが変わって候補から外れたチームは選択を解除する
  useEffect(() => {
    setSelectedTeamIds((prev) => {
      if (prev.length === 0) return prev;
      const availableIds = new Set(teamOptionsFiltered.map((t) => t.id));
      const next = prev.filter((id) => availableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [teamOptionsFiltered]);

  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [rows, setRows] = useState<SyncStatusRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [notifyOpen, setNotifyOpen] = useState<boolean>(false);
  const [notifyMsg, setNotifyMsg] = useState<string>('');
  const [lastResults, setLastResults] = useState<RejoinResult[]>([]);

  // 一覧テーブルの高さは画面サイズに合わせて可変にしつつ、再登録ボタンが画面内に
  // 収まるよう、テーブル上端の位置とボタン行の高さから利用可能な高さを都度算出する。
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [tableMaxHeight, setTableMaxHeight] = useState<number>(400);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // FastAPI 側の Optional[List[int]] は "departments=1&departments=2" の繰り返しキー形式を期待するが、
      // axios のオブジェクト params はデフォルトで "departments[]=1" 形式に直列化してしまい一致しない
      // (=フィルタが無視され全件返る)。既存の weeklySearch.ts と同様に URLSearchParams で明示的に組み立てる。
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      selectedDepts.forEach((d) => params.append('departments', String(d.id)));
      selectedTeamIds.forEach((id) => params.append('teams', String(id)));

      const res = await apiClient.get('/get_enlight_sync_status_list', { params });
      const list: SyncStatusRow[] = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      setSelected({});
      setLastResults([]);
      setTextFilter('');
      setShowUnlinkedOnly(true);
      setShowMismatchOnly(true);
      setHasSearched(true);
    } catch (e) {
      console.error('get_enlight_sync_status_list error', e);
      setLoadError('一覧の取得に失敗しました。');
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedDepts, selectedTeamIds]);

  // 初期表示は「未検索」状態とし、ユーザーが「検索」を押すまで一覧取得は行わない。

  const mismatchRows = useMemo(() => rows.filter((r) => r.mismatch), [rows]);
  // 再登録の対象は「Docset不一致」に加えて「未連携」も含める（未連携は document_id が無いだけで、
  // /rejoin_enlight_documents は document_id 無しでも現在のdocsetへ新規登録できるため流用可能）
  const actionableRows = useMemo(() => rows.filter((r) => r.mismatch || !r.linked), [rows]);

  // 一覧右上の絞り込み：いずれも取得済み一覧に対するクライアント側の絞り込みのみ（再取得はしない）
  const [textFilter, setTextFilter] = useState<string>('');
  // 「未連携のみ」「Docset不一致」はOR条件（両方ONなら、いずれかに該当する行を表示）。
  // 両方OFFの場合はこの条件による絞り込みなし（全行対象）として扱う。
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState<boolean>(true);
  const [showMismatchOnly, setShowMismatchOnly] = useState<boolean>(true);

  const quickFilteredRows = useMemo(() => {
    if (!showUnlinkedOnly && !showMismatchOnly) return rows;
    return rows.filter(
      (r) => (showUnlinkedOnly && !r.linked) || (showMismatchOnly && r.mismatch)
    );
  }, [rows, showUnlinkedOnly, showMismatchOnly]);

  const displayedRows = useMemo(() => {
    const q = textFilter.trim().toLowerCase();
    if (!q) return quickFilteredRows;
    return quickFilteredRows.filter(
      (r) => String(r.wr_id).includes(q) || (r.title ?? '').toLowerCase().includes(q)
    );
  }, [quickFilteredRows, textFilter]);

  useEffect(() => {
    const recalcTableMaxHeight = () => {
      const top = tableContainerRef.current?.getBoundingClientRect().top;
      if (top === undefined) return;
      const reservedBottom = (actionsRef.current?.offsetHeight ?? 0) + 24; // ページ下端の余白分
      const available = window.innerHeight - top - reservedBottom;
      setTableMaxHeight(Math.max(available, 160));
    };

    recalcTableMaxHeight();
    window.addEventListener('resize', recalcTableMaxHeight);
    return () => window.removeEventListener('resize', recalcTableMaxHeight);
  }, [hasSearched, loading, loadError, displayedRows.length, actionableRows.length]);

  const selectedWrIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, checked]) => Boolean(checked))
        .map(([wrId]) => Number(wrId))
        .filter((wrId) => Number.isFinite(wrId)),
    [selected]
  );

  const handleToggle = (wrId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = Boolean(e.target.checked);
    setSelected((prev) => ({ ...prev, [wrId]: checked }));
  };

  const handleToggleAll = (checked: boolean) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const next: Record<number, boolean> = {};
    actionableRows.forEach((r) => { next[r.wr_id] = true; });
    setSelected(next);
  };

  const allChecked = actionableRows.length > 0 && actionableRows.every((r) => Boolean(selected[r.wr_id]));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const targets = rows.filter((r) => selectedWrIds.includes(r.wr_id));

      // (department_id, team_id) 単位にグルーピングして、既存の rejoin_enlight_documents を組ごとに呼び出す
      const groups = new Map<string, { department_id: number; team_id: number; wr_ids: number[] }>();
      targets.forEach((r) => {
        const key = `${r.department_id}:${r.team_id}`;
        const g = groups.get(key) ?? { department_id: r.department_id, team_id: r.team_id, wr_ids: [] };
        g.wr_ids.push(r.wr_id);
        groups.set(key, g);
      });

      const allResults: RejoinResult[] = [];
      for (const g of Array.from(groups.values())) {
        try {
          const res = await apiClient.post('/rejoin_enlight_documents', {
            department_id: g.department_id,
            team_id: g.team_id,
            wr_ids: g.wr_ids,
          });
          const results: RejoinResult[] = Array.isArray(res?.data?.results) ? res.data.results : [];
          allResults.push(...results);
        } catch (e) {
          console.error('rejoin_enlight_documents error', e);
          g.wr_ids.forEach((wrId) => allResults.push({ wr_id: wrId, success: false, error: '通信エラー' }));
        }
      }

      setLastResults(allResults);
      const successCount = allResults.filter((r) => r.success).length;
      setNotifyMsg(`${successCount}/${allResults.length} 件の週報を移動しました。`);
      setNotifyOpen(true);
      await fetchList();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            絞り込み
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ width: 168 }}>
                <Typography variant="caption" color="text.secondary" sx={FILTER_LABEL_SX}>作成日時（開始）</Typography>
                <DatePicker
                  value={startDate}
                  onChange={(v) => setStartDate(v)}
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx: FILTER_CONTROL_SX } }}
                />
              </Box>
              <Box sx={{ width: 168 }}>
                <Typography variant="caption" color="text.secondary" sx={FILTER_LABEL_SX}>作成日時（終了）</Typography>
                <DatePicker
                  value={endDate}
                  onChange={(v) => setEndDate(v)}
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx: FILTER_CONTROL_SX } }}
                />
              </Box>
              <Box sx={{ width: 260 }}>
                <Typography variant="caption" color="text.secondary" sx={FILTER_LABEL_SX}>課</Typography>
                <Box
                  sx={(theme) => ({
                    // MultiSelectWithCheckboxes 自身が「.MuiOutlinedInput-root { height: 100% }」を
                    // 後勝ちで適用しており、親(FormControl)に高さの定義が無いと height:100% は
                    // auto と等価になり文字サイズ分(約23px)に潰れる。height ではなく min-height で
                    // 下限を強制することで、他項目と同じ 40px の見た目に揃える。
                    '& .MuiInputBase-root': { minHeight: 40 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.mode === 'dark' ? '#111a2b' : '#fff',
                    },
                    '& .MuiOutlinedInput-root fieldset': {
                      borderColor: theme.palette.mode === 'dark' ? '#26384f' : '#e0e0e0',
                    },
                  })}
                >
                  <MultiSelectWithCheckboxes
                    suggestions={departmentSuggestions}
                    placeholder="条件なし"
                    initialValue={EMPTY_SUGGESTIONS}
                    onChange={setSelectedDepts}
                  />
                </Box>
              </Box>
              <Box sx={{ width: 260 }} title={selectedDepts.length === 0 ? '先に課を選択してください' : undefined}>
                <Typography variant="caption" color="text.secondary" sx={FILTER_LABEL_SX}>チーム</Typography>
                <Box
                  data-testid="enlight-team-select"
                  sx={{
                    // MultiSelectCheckbox 自前のラベル・上マージンは、他の項目とラベル位置を揃えるため非表示にする
                    '& .label-check': { display: 'none' },
                    '& #select-check': { margin: 0 },
                    '& .MuiInputBase-root': { height: 40, minHeight: '40px !important' },
                    '& .MuiSelect-select': { paddingTop: '8.5px', paddingBottom: '8.5px', minHeight: 'unset !important' },
                  }}
                >
                  <MultiSelectCheckbox
                    label="チーム"
                    options={teamOptionsFiltered}
                    selectedValues={selectedTeamIds}
                    onChange={setSelectedTeamIds}
                    disabled={selectedDepts.length === 0}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={FILTER_LABEL_SX} aria-hidden="true">&nbsp;</Box>
                <Button variant="contained" onClick={fetchList} disabled={loading} sx={{ height: 40 }}>
                  検索
                </Button>
              </Box>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Enlight連携ONの週報一覧
              {hasSearched && !loading && !loadError ? `（${rows.length}件、不一致 ${mismatchRows.length}件）` : ''}
            </Typography>
            {hasSearched && !loading && !loadError && rows.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={showUnlinkedOnly}
                      onChange={(e) => setShowUnlinkedOnly(e.target.checked)}
                    />
                  }
                  label="未連携のみ"
                />
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={showMismatchOnly}
                      onChange={(e) => setShowMismatchOnly(e.target.checked)}
                    />
                  }
                  label="Docset不一致"
                />
                <TextField
                  size="small"
                  placeholder="週報ID・タイトルで絞り込み"
                  value={textFilter}
                  onChange={(e) => setTextFilter(e.target.value)}
                  sx={{ width: 260 }}
                />
              </Box>
            )}
          </Box>

          {!hasSearched ? (
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>
              絞り込み条件を指定し、「検索」を押してください。
            </Box>
          ) : loading ? (
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>読込中...</Box>
          ) : loadError ? (
            <Box sx={{ fontSize: 14, color: 'error.main' }}>{loadError}</Box>
          ) : rows.length === 0 ? (
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>該当する週報はありません。</Box>
          ) : displayedRows.length === 0 ? (
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>絞り込み条件に一致する週報はありません。</Box>
          ) : (
            <TableContainer
              ref={tableContainerRef}
              component={Paper}
              elevation={0}
              sx={{ maxHeight: tableMaxHeight, overflow: 'auto' }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        onChange={(e) => handleToggleAll(e.target.checked)}
                        disabled={actionableRows.length === 0 || submitting}
                      />
                    </TableCell>
                    <TableCell>週報ID</TableCell>
                    <TableCell>タイトル</TableCell>
                    <TableCell>課</TableCell>
                    <TableCell>チーム</TableCell>
                    <TableCell>作成日時</TableCell>
                    <TableCell>連携状態</TableCell>
                    <TableCell>Docset不一致</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRows.map((row) => {
                    const docsetStatus = !row.linked
                      ? { label: '-', color: 'default' as const }
                      : row.current_docset_id === null
                        ? { label: '連携先未設定', color: 'default' as const }
                        : row.mismatch
                          ? { label: '不一致', color: 'error' as const }
                          : { label: '一致', color: 'success' as const };

                    return (
                      <TableRow key={row.wr_id}>
                        <TableCell padding="checkbox">
                          {(row.mismatch || !row.linked) && (
                            <Checkbox
                              size="small"
                              checked={Boolean(selected[row.wr_id])}
                              onChange={handleToggle(row.wr_id)}
                              disabled={submitting}
                            />
                          )}
                        </TableCell>
                        <TableCell>{row.wr_id}</TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell>{departmentLabelById.get(row.department_id) ?? row.department_id}</TableCell>
                        <TableCell>{teamLabelById.get(row.team_id) ?? row.team_id}</TableCell>
                        <TableCell>{formatVersionDt(row.create_dt)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.linked ? '連携済み' : '未連携'}
                            color={row.linked ? 'success' : 'default'}
                            variant={row.linked ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={docsetStatus.label} color={docsetStatus.color} variant="outlined" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {actionableRows.length > 0 && (
            <Box ref={actionsRef} sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                disabled={submitting || selectedWrIds.length === 0}
                onClick={() => setConfirmOpen(true)}
              >
                選択した週報を再登録する（{selectedWrIds.length}件）
              </Button>
            </Box>
          )}

          {lastResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>実行結果</Typography>
              <Table size="small">
                <TableBody>
                  {lastResults.map((r) => (
                    <TableRow key={r.wr_id}>
                      <TableCell padding="checkbox">
                        {r.success ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>週報ID: {r.wr_id}</TableCell>
                      <TableCell>{r.success ? '再登録しました' : (r.error ?? '失敗しました')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="選択した週報を再登録する"
        description={`選択した ${selectedWrIds.length} 件の週報を、現在の連携先(docset)へ再登録（旧Documentの削除と新規作成）します。よろしいですか？`}
        confirmText="再登録する"
        cancelText="キャンセル"
        processing={submitting}
        onConfirm={() => {
          setConfirmOpen(false);
          handleSubmit();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <Snackbar open={notifyOpen} message={notifyMsg} onClose={() => setNotifyOpen(false)} />
    </Box>
  );
};

export default EnlightSyncCheck;
